import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";
import { fetchClassificationResultById, fetchLatestClassificationResult } from "./useLatestClassificationResult";

export class HumanReviewNotFoundError extends Error {
  constructor(message = "Human review not found") {
    super(message);
    this.name = "HumanReviewNotFoundError";
    Object.setPrototypeOf(this, HumanReviewNotFoundError.prototype);
  }
}

// Type aliases for Supabase generated types
export type InboundProcedure = Database["public"]["Tables"]["inbound_classification_result_procedures"]["Row"];
export type InboundDiagnosis = Database["public"]["Tables"]["inbound_classification_result_diagnosis"]["Row"];
type InboundFile = Database["public"]["Tables"]["inbound_classification_files"]["Row"];
type EncounterHumanReview = Database["public"]["Tables"]["encounter_human_review"]["Row"];
type EndoscopyResult = Database["public"]["Tables"]["inbound_endoscopy_specific_results"]["Row"];
type Customer = Database["public"]["Tables"]["customers"]["Row"];
type PatientEncounterRow = Database["public"]["Tables"]["patient_encounters"]["Row"];

export type EncounterHumanCodingReviewData = {
  encounterId: string;
  reviewId: string;
  reviewerId: string;
  reviewStatus: EncounterHumanReview["status"];
  reviewOutcome: EncounterHumanReview["outcome"];
  customer: Customer;
  specialty: PatientEncounterRow["specialty"];
  patientAge: number;
  patientSex: Database["public"]["Enums"]["patient_sexes"];
  admitDatetime: string;
  dischargeDatetime: string;
  notes: string | null;
  procedures: {
    code: string;
    description: string;
  }[];
  diagnoses: {
    code: string;
    description: string;
  }[];
  hrg?: {
    id: string;
    code: string;
  };
  latestClassificationResult: {
    id: string;
    runId: string | null;
    langfuseTraceId: string | null;
    hrgId: string | null;
    outcome: Database["public"]["Enums"]["classification_result_outcome"];
    isFollowUp: boolean;
    humanFlagForReview: boolean;
    aiFlagForReview: boolean;
    aiFlagForReviewText: string | null;
    classifiedAt: string;
    endoscopyResult?: {
      wasSpecimenOrHistologyTaken: boolean;
      primaryDiagnosisIdentified: boolean | null;
    };
    procedures: {
      code: string;
      description: string;
      aiFlagText?: string | null;
    }[];
    diagnoses: {
      code: string;
      description: string;
      isHistoricCode: boolean;
    }[];
  };
};

export const useEncounterHumanCodingReviewData = (encounterId?: string, classificationResultId?: string) => {
  const { data, isLoading, error } = useQuery<EncounterHumanCodingReviewData, Error>({
    queryKey: ["encounter-human-coding-review", encounterId, classificationResultId],
    queryFn: async () => {
      if (!encounterId) throw new Error("Encounter ID is required");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const classificationResultPromise = classificationResultId
        ? fetchClassificationResultById(classificationResultId)
        : fetchLatestClassificationResult(encounterId);

      // Fetch patient encounter with related data
      const { data: encounter, error: encounterError } = await supabase
        .from("patient_encounters")
        .select(
          `
          *,
          customer:customers(*),
          humanReviews:encounter_human_review(
            *,
            procedures:encounter_human_review_procedure_code(*),
            diagnoses:encounter_human_review_diagnosis_code(*),
            hrg:hrgs(*)
          )
        `,
        )
        .eq("id", encounterId)
        .single();

      if (encounterError) throw encounterError;
      if (!encounter) throw new Error("Encounter not found");
      if (!encounter.customer) throw new Error("Customer not found");

      // Load the current user's review only (encounter can have multiple reviews from different reviewers)
      const humanReview = encounter.humanReviews.find((r) => r.reviewer_id === user.id);

      if (!humanReview) throw new HumanReviewNotFoundError();

      const classificationResult = await classificationResultPromise;

      if (!classificationResult) {
        throw new Error("Classification result not found");
      }

      return {
        encounterId: encounter.id,
        reviewId: humanReview.id,
        reviewerId: humanReview.reviewer_id,
        reviewStatus: humanReview.status,
        reviewOutcome: humanReview.outcome,
        customer: encounter.customer,
        specialty: encounter.specialty,
        patientAge: encounter.patient_age,
        patientSex: encounter.patient_sex,
        admitDatetime: encounter.admit_datetime ?? "",
        dischargeDatetime: encounter.discharge_datetime ?? "",
        notes: humanReview.notes,
        procedures: (humanReview.procedures || [])
          .slice()
          .sort((a, b) => a.order_index - b.order_index)
          .map((p) => ({
            code: p.code,
            description: p.description ?? "",
          })),
        diagnoses: (humanReview.diagnoses || [])
          .slice()
          .sort((a, b) => a.order_index - b.order_index)
          .map((d) => ({
            code: d.code,
            description: d.description ?? "",
          })),
        hrg: humanReview.hrg
          ? {
              id: humanReview.hrg.id,
              code: humanReview.hrg.code,
            }
          : undefined,
        latestClassificationResult: {
          id: classificationResult.id,
          runId: classificationResult.runId,
          langfuseTraceId: classificationResult.langfuseTraceId,
          hrgId: classificationResult.hrgId,
          outcome: classificationResult.outcome,
          isFollowUp: classificationResult.isFollowUp,
          humanFlagForReview: classificationResult.humanFlagForReview,
          aiFlagForReview: classificationResult.aiFlagForReview,
          aiFlagForReviewText: classificationResult.aiFlagForReviewText,
          classifiedAt: classificationResult.classifiedAt,
          endoscopyResult: classificationResult.endoscopyResult,
          procedures: classificationResult.procedures,
          diagnoses: classificationResult.diagnoses,
        },
      };
    },
    enabled: !!encounterId,
  });

  return {
    data,
    isLoading,
    error,
  };
};
